/**
 * Transform backend tracker entries to frontend ManualEntry format.
 *
 * Backend returns flat entries with payslip_id:
 * - {id, date, type: "income", amount, payslip_id}
 * - {id, date, type: "income_tax", amount, payslip_id}
 * - {id, date, type: "national_insurance", amount, payslip_id}
 * - {id, date, type: "contribution", amount, contribution_type, payment_method, payslip_id: null}
 *
 * Frontend expects:
 * - {id, kind: "income", date, gross, tax, ni} for grouped payslip entries
 * - {id, kind: "income", date, gross, tax: 0, ni: 0} for standalone income
 * - {id, kind: "contribution", subtype, source, date, amount}
 */

import type { BackendEntry } from '../types/household';
import type { ManualEntry } from '../types/tracking';

export function transformTrackerEntries(backendEntries: BackendEntry[]): ManualEntry[] {
  const result: ManualEntry[] = [];

  // Group entries by payslip_id
  const payslipGroups = new Map<string, {
    income?: BackendEntry;
    tax?: BackendEntry;
    ni?: BackendEntry;
    taxableBenefits?: BackendEntry;
  }>();

  const standaloneEntries: BackendEntry[] = [];

  // First pass: separate payslip entries from standalone entries
  for (const entry of backendEntries) {
    if (entry.type === 'contribution') {
      // Contributions are always standalone
      standaloneEntries.push(entry);
    } else if (entry.payslip_id) {
      // Entry belongs to a payslip - add to group
      if (!payslipGroups.has(entry.payslip_id)) {
        payslipGroups.set(entry.payslip_id, {});
      }
      const group = payslipGroups.get(entry.payslip_id)!;

      if (entry.type === 'income') {
        group.income = entry;
      } else if (entry.type === 'income_tax') {
        group.tax = entry;
      } else if (entry.type === 'national_insurance') {
        group.ni = entry;
      } else if (entry.type === 'taxable_benefit') {
        group.taxableBenefits = entry;
      }
    } else {
      // Entry without payslip_id - standalone
      standaloneEntries.push(entry);
    }
  }

  // Second pass: create grouped payslip entries
  for (const [payslipId, group] of payslipGroups.entries()) {
    const { income, tax, ni, taxableBenefits } = group;

    // Use income entry as the primary entry (has the date and id)
    if (income) {
      result.push({
        id: income.id,
        kind: 'income',
        date: income.date,
        gross: parseFloat(income.amount),
        tax: tax ? parseFloat(tax.amount) : 0,
        ni: ni ? parseFloat(ni.amount) : 0,
        taxableBenefits: taxableBenefits ? parseFloat(taxableBenefits.amount) : undefined,
        groupId: payslipId, // Track which payslip this came from
        taxCode: income.payslip_tax_code ?? undefined,
        taxCodeWarnings: income.payslip_tax_code_warnings ?? undefined,
      });
    }
  }

  // Third pass: add standalone entries
  for (const entry of standaloneEntries) {
    if (entry.type === 'contribution') {
      if (!entry.contribution_type) {
        console.error('Backend contribution entry missing contribution_type:', entry);
        continue;
      }
      result.push({
        id: entry.id,
        kind: 'contribution',
        subtype: entry.contribution_type,
        source: entry.source || 'user', // Use backend source field
        date: entry.date,
        amount: parseFloat(entry.amount),
      });
    } else if (entry.type === 'income') {
      result.push({
        id: entry.id,
        kind: 'income',
        date: entry.date,
        gross: parseFloat(entry.amount),
        tax: 0,
        ni: 0,
      });
    } else if (entry.type === 'income_tax') {
      // Standalone tax entry (shouldn't normally happen, but handle it)
      result.push({
        id: entry.id,
        kind: 'income',
        date: entry.date,
        gross: 0,
        tax: parseFloat(entry.amount),
        ni: 0,
      });
    } else if (entry.type === 'national_insurance') {
      // Standalone NI entry (shouldn't normally happen, but handle it)
      result.push({
        id: entry.id,
        kind: 'income',
        date: entry.date,
        gross: 0,
        tax: 0,
        ni: parseFloat(entry.amount),
      });
    }
  }

  // Sort by date descending (most recent first)
  result.sort((a, b) => b.date.localeCompare(a.date));

  return result;
}
